/**
 * SUGGESTIONS UNDER THE BOX SOMEBODY IS TYPING IN.
 *
 * Anthony: "Allow it to autofill or auto populate one of these when someone
 * goes to type in a part along with any saved parts or labor items we've
 * done."
 *
 * ─── WHY IT IS NOT A <datalist> ────────────────────────────────────────
 *
 * A datalist would have been four lines. It also cannot show what the shop
 * paid, cannot say whether a suggestion is something fitted before or a
 * word from the reference, renders differently in every browser, and on
 * iOS is close to invisible. The whole value here is the difference
 * between "we have fitted this, it cost $34" and "this is a phrase" —
 * flattening that away leaves a list of words nobody trusts.
 *
 * ─── IT NEVER TYPES FOR YOU ────────────────────────────────────────────
 *
 * No inline completion, no first-match-wins on blur. An advisor typing
 * "front brake" and looking away must not find the box says something
 * else. A suggestion is applied when somebody chooses it, by press or by
 * Enter on a highlighted row, and never otherwise.
 *
 * The spelling fix is the one exception, and it is applied on BLUR rather
 * than while typing: "breaks" becomes "Brakes" once you have left the
 * field, because correcting a word underneath a cursor is how a person
 * loses their place.
 */

import { db } from "../data/client.ts";
import { esc, money } from "./dom.ts";
import { suggest, fixSpelling, type ServiceTerm, type Suggestion }
  from "../domain/services.ts";

let terms: ServiceTerm[] | null = null;
let fixes: Map<string, string> | null = null;

/**
 * Loaded once per session and kept. 158 rows is nothing, and asking for
 * them on every keystroke would be a request per letter for a list that
 * does not change from one month to the next.
 */
export async function loadVocabulary(): Promise<void> {
  if (terms && fixes) return;
  try {
    const sb = await db();
    const [t, f] = await Promise.all([
      sb.from("service_terms").select("term,section,aliases,seen").order("seen", { ascending: false }),
      sb.from("spelling_fixes").select("typed,store_as"),
    ]);
    terms = ((t.data ?? []) as ServiceTerm[]);
    fixes = new Map(((f.data ?? []) as Array<{ typed: string; store_as: string }>)
      .map((x) => [x.typed.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim(), x.store_as]));
  } catch {
    // A shop with no suggestions is a shop typing by hand, which is what it
    // did last week. Not worth an error over a quote screen.
    terms = terms ?? [];
    fixes = fixes ?? new Map();
  }
}

export type TypeaheadOpts = {
  /** What the shop has already fitted or quoted, ranked above the reference. */
  done?: () => Suggestion[];
  /** Told what was chosen, so a part can bring its cost and number with it. */
  onPick?: (s: Suggestion) => void;
};

/**
 * Attach to one input. Safe to call again on the same element: the marker
 * stops a second listener stacking up when a list is redrawn.
 */
export function typeahead(input: HTMLInputElement, opts: TypeaheadOpts = {}): void {
  if (input.dataset["ta"] === "on") return;
  input.dataset["ta"] = "on";
  input.setAttribute("autocomplete", "off");

  let box: HTMLDivElement | null = null;
  let rows: Suggestion[] = [];
  let at = -1;

  const shut = (): void => { box?.remove(); box = null; rows = []; at = -1; };

  const place = (): void => {
    if (!box) return;
    const r = input.getBoundingClientRect();
    box.style.left = `${r.left + window.scrollX}px`;
    box.style.top = `${r.bottom + window.scrollY + 4}px`;
    box.style.width = `${Math.max(r.width, 240)}px`;
  };

  const draw = (): void => {
    if (!rows.length) { shut(); return; }
    if (!box) {
      box = document.createElement("div");
      box.className = "tahead";
      // On the body rather than beside the input: the line editor rows have
      // their own overflow, and a list clipped by its own row is a list
      // nobody can read past the second entry.
      document.body.appendChild(box);
    }
    box.innerHTML = rows.map((s, i) => `
      <button class="ta-row${i === at ? " on" : ""}" data-i="${i}">
        <span class="ta-l">${esc(s.label)}</span>
        <span class="ta-r">${s.from === "reference"
          ? esc(s.hint ?? "")
          : s.cost != null ? `${s.from === "fitted" ? "fitted" : "quoted"} &middot; ${
              esc(money(s.cost))}`
          : s.hours != null ? `${s.hours} hr`
          : esc(s.from)}</span>
      </button>`).join("");
    place();
    box.querySelectorAll<HTMLButtonElement>(".ta-row").forEach((b) =>
      b.addEventListener("mousedown", (e) => {
        // mousedown, not click: blur fires first and would shut the list
        // before a click ever lands on it.
        e.preventDefault();
        take(rows[Number(b.dataset["i"])] as Suggestion);
      }));
  };

  const take = (s: Suggestion): void => {
    if (!s) return;
    input.value = s.label;
    shut();
    opts.onPick?.(s);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  input.addEventListener("input", () => {
    rows = suggest(input.value, {
      terms: terms ?? [], done: opts.done?.() ?? [], limit: 8,
    });
    at = -1;
    draw();
  });

  input.addEventListener("keydown", (e) => {
    if (!rows.length) return;
    const k = (e as KeyboardEvent).key;
    if (k === "ArrowDown") { e.preventDefault(); at = Math.min(at + 1, rows.length - 1); draw(); }
    else if (k === "ArrowUp") { e.preventDefault(); at = Math.max(at - 1, -1); draw(); }
    else if (k === "Escape") { shut(); }
    else if (k === "Enter" && at >= 0) {
      // Only a HIGHLIGHTED row is taken. Enter with nothing chosen means
      // what it means everywhere else on this screen: keep what I typed.
      e.preventDefault();
      take(rows[at] as Suggestion);
    }
  });

  input.addEventListener("blur", () => {
    // The spelling correction lands here, once the cursor has gone.
    const fixed = fixes ? fixSpelling(input.value, fixes) : input.value;
    if (fixed !== input.value) {
      input.value = fixed;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    setTimeout(shut, 120);
  });

  window.addEventListener("scroll", () => { if (box) place(); }, { passive: true });
}
